import { useEffect } from 'react';

type SeoConfig = {
  description: string;
  image?: string;
  keywords?: string | string[];
  locale?: string;
  noindex?: boolean;
  path?: string;
  siteName?: string;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
  title: string;
  twitterHandle?: string;
  type?: 'article' | 'website';
};

const DEFAULT_OG_IMAGE_PATH = '/car-dealer-hero.png';
const SITE_ORIGIN = 'https://www.profit-cars.com';

function upsertMeta(
  selector: string,
  attrs: { name?: string; property?: string },
  content: string
) {
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!tag) {
    tag = document.createElement('meta');
    if (attrs.name) {
      tag.setAttribute('name', attrs.name);
    }
    if (attrs.property) {
      tag.setAttribute('property', attrs.property);
    }
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let tag = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
}

function normalizeStructuredData(
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
) {
  if (!structuredData) {
    return [];
  }

  const entries = Array.isArray(structuredData) ? structuredData : [structuredData];

  return entries
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && !Array.isArray(entry))
    .map((entry) => {
      if (typeof entry['@context'] === 'string' && entry['@context'].trim()) {
        return entry;
      }

      return {
        '@context': 'https://schema.org',
        ...entry,
      };
    });
}

function upsertStructuredData(
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
) {
  const scriptIdPrefix = 'seo-structured-data-';
  const existingScripts = document.head.querySelectorAll(`[id^="${scriptIdPrefix}"]`);
  existingScripts.forEach((script) => script.remove());

  const normalizedData = normalizeStructuredData(structuredData);

  if (!normalizedData.length) {
    return;
  }

  normalizedData.forEach((entry, index) => {
    const script = document.createElement('script');
    script.id = `${scriptIdPrefix}${index}`;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(entry);
    document.head.appendChild(script);
  });
}

export function useSeo({
  description,
  image,
  keywords,
  locale = 'en_US',
  noindex = false,
  path,
  siteName = 'Profit Cars',
  structuredData,
  title,
  twitterHandle,
  type = 'website',
}: SeoConfig) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const resolvedPath = path || window.location.pathname;
    const pageUrl = new URL(resolvedPath, SITE_ORIGIN).toString();
    const resolvedImage = new URL(image || DEFAULT_OG_IMAGE_PATH, SITE_ORIGIN).toString();
    const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';
    const resolvedKeywords = Array.isArray(keywords) ? keywords.join(', ') : keywords;

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, robotsContent);
    if (resolvedKeywords) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords' }, resolvedKeywords);
    }

    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, type);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, pageUrl);
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, siteName);
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, locale);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, resolvedImage);

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, resolvedImage);
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url' }, pageUrl);
    if (twitterHandle) {
      upsertMeta('meta[name="twitter:site"]', { name: 'twitter:site' }, twitterHandle);
      upsertMeta('meta[name="twitter:creator"]', { name: 'twitter:creator' }, twitterHandle);
    }

    upsertCanonical(pageUrl);
    upsertStructuredData(structuredData);
  }, [description, image, keywords, locale, noindex, path, siteName, structuredData, title, twitterHandle, type]);
}
